/* Batch 11 · Stage A — the sign-off bar (owner decisions D1–D11, 10-Oct-2026).
   One bar at the top of every form, vouching sheet, sampling sheet and worksheet: a tick per level (Preparer, Reviewer 1,
   Reviewer 2 / 3 when switched on, EQ reviewer when the engagement requires an EQ review, Partner) with the signer's name and date.
   Sign → the date defaults to today and stays editable (D3, no "recorded at"); only the signer may change the date or remove
   (D5); remove and sign again at every level (D4). The server refuses Preparer + Reviewer 1 by one person (D6) and every
   action on a locked file. Flags: "Changed after sign-off · date" (D7) and "Signed but empty" (D8).
   Props: itemType, itemKey, refreshKey (change it after a save so the flags are re-read), compact. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { toErrMsg, ITEM_SAVED_EVENT } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { fmtDate, fmtDateTimeFirm } from "@/lib/dt";
import DateInput from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { ReviewControls, ReviewCountsProvider } from "@/components/reviewpoints/ReviewPointsPanel";   // Batch 28 · item 8 (M54)

// the sign-off items a review point can be raised on (server: review_point_targets.ITEM_TARGETS)
export const RP_ITEM_TYPES = new Set(["procedures", "lead_sheet", "hf_worksheet", "completeness", "inventory_count", "estimate",
  "related_party", "sampling", "a9001", "a9002", "a9003", "w9", "w10"]);

// item keys of the one-per-engagement A-series pages (the server's signoffs.list_items uses the same keys); kept here so the
// pages carry no visible "A9001" literal (Batch B3 · Z6 — the codes are never shown on those pages)
export const A_SERIES_KEY = { a9001: "A9001", a9002: "A9002", a9003: "A9003-01" };

// Batch 28 · Follow-up 1 · F2: ``saveFirst`` (the page has unsaved changes on its Save / Cancel bar) keeps Sign disabled until saved.
export default function SignOffBar({ itemType, itemKey, refreshKey, engId: engIdProp, label, onChange, missing, saveFirst = false }) {
  const { t, lang } = useI18n();
  const { currentEngId } = useEngagement();
  const engId = engIdProp || currentEngId;
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState("");
  const [workDate, setWorkDate] = useState({});   // Batch 14 · M5 (F-104): optional "date the work was done" per level

  // Step 11c-3 · find 42 (Mars live, 308(a) / 310(a)): Remove then Sign sometimes left the level unsigned until the panel was
  // refreshed — several reads of the bar run at once (the bar's own re-read after an action, the item's refreshKey, the saved-
  // change event) and an OLDER read that answered last overwrote the newer state. Only the newest read may set the bar.
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!engId || !itemType || !itemKey) return;
    const mine = ++seq.current;
    try {
      const r = await api.get(`/engagements/${engId}/signoffs/item`, { params: { item_type: itemType, item_key: itemKey }, _silent: true });
      if (mine !== seq.current) return;                       // a newer read was started meanwhile: this answer is stale
      setSt(r.data && r.data.exists !== false && Array.isArray(r.data.levels) ? r.data : null);   // no bar until the item exists
    } catch (_) { if (mine === seq.current) setSt(null); }
  }, [engId, itemType, itemKey]);

  useEffect(() => { load(); }, [load, refreshKey]);
  // Batch 12 · L4 (D7): re-read the flags right after any save on screen (debounced; the bar's own actions excluded)
  useEffect(() => {
    let timer = null;
    const onSaved = () => { clearTimeout(timer); timer = setTimeout(() => { load(); }, 300); };
    const evt = ITEM_SAVED_EVENT || "af-item-saved";   // (a test mock of the api module may not carry the name)
    window.addEventListener(evt, onSaved);
    return () => { clearTimeout(timer); window.removeEventListener(evt, onSaved); };
  }, [load]);

  const act = async (path, level, extra = {}) => {
    setBusy(level);
    try {
      const r = await api.post(`/engagements/${engId}/signoffs/${path}`, { item_type: itemType, item_key: itemKey, level, ...extra });
      if (r.data?.warning) toast.warning(t("itemSignoff.emptyWarning"));
      setEditing("");
      await load();
      if (onChange) onChange(path, level, r.data);     // Batch 27 · C (M38): the item re-reads its Mark complete gate
    } catch (e) {
      toast.error(toErrMsg(e, t("itemSignoff.failed")));
    } finally { setBusy(""); }
  };

  if (!st || !Array.isArray(st.levels)) return null;
  return (
    <div className="mb-3 flex flex-wrap items-stretch gap-2 rounded border bg-slate-50 p-2 text-xs dark:bg-slate-900" data-testid="signoff-bar"
         data-item={`${itemType}:${itemKey}`}>
      {label && <div className="w-full text-[11px] font-semibold text-slate-600" data-testid="signoff-bar-label">{label}</div>}
      {RP_ITEM_TYPES.has(itemType) && engId && (
        <div className="flex w-full justify-end" data-testid="signoff-bar-rp">
          <ReviewCountsProvider engagementId={engId} targetType={itemType}>
            <ReviewControls engagementId={engId} targetType={itemType} targetId={itemKey} targetLabel={label || itemKey} compact />
          </ReviewCountsProvider>
        </div>
      )}
      {st.levels.map((lv) => {
        const so = lv.signoff;
        return (
          // Batch 27 · Add-on 1: a level the server says is missing ("signoff.<level>") is marked red, with its reason
          <div key={lv.level} className={`min-w-[270px] flex-1 rounded border px-2 py-1 ${so ? "border-emerald-300 bg-white dark:bg-slate-950" : "border-dashed"} ${!so && missing?.[`signoff.${lv.level}`] ? "border-solid border-destructive ring-1 ring-destructive" : ""}`}
               data-testid={`so-level-${lv.level}`} data-field={`signoff.${lv.level}`}
               aria-invalid={!so && missing?.[`signoff.${lv.level}`] ? true : undefined}>
            <div className="flex items-center gap-1 font-medium">
              {so ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Circle className="h-3.5 w-3.5 text-slate-400" />}
              {t(`itemSignoff.level.${lv.level}`)}
            </div>
            {so ? (
              <div className="mt-0.5 space-y-0.5">
                <div data-testid={`so-name-${lv.level}`}>{so.name}</div>
                {so.signed_at && <div className="text-[10px] text-slate-500" data-testid={`so-signed-at-${lv.level}`}>{t("itemSignoff.clickedAt")} {fmtDateTimeFirm(so.signed_at)}</div>}
                {editing === lv.level ? (
                  <DateInput value={so.date} onChange={(v) => v && act("date", lv.level, { date: v })} data-testid={`so-date-input-${lv.level}`} />
                ) : (
                  <button type="button" className={`text-slate-600 ${lv.can_edit ? "underline decoration-dotted" : ""}`} disabled={!lv.can_edit}
                          onClick={() => setEditing(lv.level)} data-testid={`so-date-${lv.level}`} title={lv.can_edit ? t("itemSignoff.editDate") : ""}>
                    {fmtDate(so.date)}
                  </button>
                )}
                {so.changed_after_signoff && (
                  <div className="flex items-center gap-1 text-amber-700" data-testid={`so-flag-changed-${lv.level}`}>
                    <AlertTriangle className="h-3 w-3" />{t("itemSignoff.changedAfter")} · {fmtDate(so.changed_after_signoff)}
                  </div>
                )}
                {so.signed_but_empty && (
                  <div className="flex items-center gap-1 text-amber-700" data-testid={`so-flag-empty-${lv.level}`}>
                    <AlertTriangle className="h-3 w-3" />{t("itemSignoff.signedEmpty")}
                  </div>
                )}
                {lv.can_edit && (
                  <button type="button" className="inline-flex items-center gap-0.5 text-red-700 underline" disabled={!!busy}
                          onClick={() => act("remove", lv.level)} data-testid={`so-remove-${lv.level}`}>
                    <X className="h-3 w-3" />{t("itemSignoff.remove")}
                  </button>
                )}
              </div>
            ) : (
              lv.can_sign ? (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <DateInput value={workDate[lv.level] || ""} onChange={(v) => setWorkDate((w) => ({ ...w, [lv.level]: v }))} data-testid={`so-work-date-${lv.level}`} placeholder={t("itemSignoff.workDate")} />
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={!!busy || saveFirst} title={saveFirst ? (lang === "ar" ? "احفظ التغييرات أولاً" : "Save the changes first") : undefined}
                          onClick={() => act("sign", lv.level, workDate[lv.level] ? { date: workDate[lv.level] } : {})} data-testid={`so-sign-${lv.level}`}>{t("itemSignoff.sign")}</Button>
                </div>
              ) : (
                <div className="mt-0.5 text-slate-400" title={(lang === "ar" && lv.why_not_ar) || lv.why_not || ""} data-testid={`so-empty-${lv.level}`}>—</div>
              )
            )}
            {!so && missing?.[`signoff.${lv.level}`] && (
              <div role="alert" className="mt-1 text-[11px] text-destructive" data-testid={`so-missing-${lv.level}`}>
                {lang === "ar" ? missing[`signoff.${lv.level}`].reason_ar : missing[`signoff.${lv.level}`].reason_en}
              </div>
            )}
          </div>
        );
      })}
      {st.locked && <div className="self-center text-slate-500" data-testid="signoff-locked">{t("itemSignoff.locked")}</div>}
    </div>
  );
}
